import type { ReactNode, ChangeEvent } from 'react';
import { ADMIN_COLORS } from '@/features/admin/utils/adminAnimations';

/* ------------------------------------------------------------------ */
/*  Shared style tokens                                                */
/* ------------------------------------------------------------------ */

const inputBase =
  'px-3 py-1.5 rounded-lg text-sm border border-white/30 bg-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-shadow';

const labelBase =
  'flex items-center gap-2 text-xs font-semibold whitespace-nowrap';

/* ------------------------------------------------------------------ */
/*  ColorControl                                                       */
/* ------------------------------------------------------------------ */

interface ColorControlProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
}

export function ColorControl({ label, value, onChange }: ColorControlProps) {
  return (
    <label className={labelBase} style={{ color: ADMIN_COLORS.textSecondary }}>
      {label}
      <input
        type="color"
        value={value.startsWith('#') ? value : '#888888'}
        onChange={(e) => onChange(e.target.value)}
        className="w-7 h-7 rounded cursor-pointer border border-white/30 bg-transparent"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputBase} w-24 font-mono text-[11px]`}
      />
    </label>
  );
}

/* ------------------------------------------------------------------ */
/*  SliderControl                                                      */
/* ------------------------------------------------------------------ */

interface SliderControlProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}

export function SliderControl({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  unit = '',
}: SliderControlProps) {
  return (
    <label className={labelBase} style={{ color: ADMIN_COLORS.textSecondary }}>
      {label}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-28 accent-[var(--color-primary-500)] cursor-pointer"
      />
      <span className="text-[11px] tabular-nums font-mono min-w-[3ch]">
        {value}{unit}
      </span>
    </label>
  );
}

/* ------------------------------------------------------------------ */
/*  TextControl                                                        */
/* ------------------------------------------------------------------ */

interface TextControlProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  className?: string;
}

export function TextControl({ label, value, onChange, className }: TextControlProps) {
  return (
    <label className={`${labelBase} ${className ?? ''}`} style={{ color: ADMIN_COLORS.textSecondary }}>
      {label}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputBase} w-36`}
      />
    </label>
  );
}

/* ------------------------------------------------------------------ */
/*  ToggleControl                                                      */
/* ------------------------------------------------------------------ */

interface ToggleControlProps {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}

export function ToggleControl({ label, value, onChange }: ToggleControlProps) {
  return (
    <label className={`${labelBase} cursor-pointer`} style={{ color: ADMIN_COLORS.textSecondary }}>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-[var(--color-primary-500)]"
      />
      {label}
    </label>
  );
}

/* ------------------------------------------------------------------ */
/*  SelectControl                                                      */
/* ------------------------------------------------------------------ */

interface SelectControlProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { label: string; value: string }[];
}

export function SelectControl({ label, value, onChange, options }: SelectControlProps) {
  return (
    <label className={labelBase} style={{ color: ADMIN_COLORS.textSecondary }}>
      {label}
      <select
        value={value}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
        className={`${inputBase} cursor-pointer`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ------------------------------------------------------------------ */
/*  SectionHeader                                                      */
/* ------------------------------------------------------------------ */

export function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <h3
      className="mb-2 text-xs font-bold uppercase tracking-wider"
      style={{ color: ADMIN_COLORS.textSecondary }}
    >
      {children}
    </h3>
  );
}

/* ------------------------------------------------------------------ */
/*  PreviewBox                                                         */
/* ------------------------------------------------------------------ */

export function PreviewBox({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`p-4 rounded-xl ${className ?? ''}`}
      style={{
        backgroundColor: ADMIN_COLORS.ice,
        border: `1px dashed ${ADMIN_COLORS.border}`,
      }}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ControlsPanel                                                      */
/* ------------------------------------------------------------------ */

export function ControlsPanel({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-x-5 gap-y-3 items-center">{children}</div>;
}

/* ------------------------------------------------------------------ */
/*  ResetButton                                                        */
/* ------------------------------------------------------------------ */

export function ResetButton({ onClick, label = 'Reset' }: { onClick: () => void; label?: string }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/50 border border-white/30 hover:bg-white/70 transition-colors cursor-pointer"
      style={{ color: ADMIN_COLORS.textSecondary }}
    >
      {label}
    </button>
  );
}
